import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import TurnstileWidget from "@/components/TurnstileWidget";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password, turnstileToken);
      const next = sp.get("next");
      nav(next || (u.role === "admin" ? "/admin" : u.role === "vendor" ? "/vendor" : "/account"));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="login-page">
      <p className="overline text-center">Welcome back</p>
      <h1 className="font-display text-4xl text-center mt-3">Sign in to your kitchen.</h1>

      <div className="mt-10">
        <GoogleAuthButton />
      </div>

      <div className="my-6 flex items-center gap-3">
        <span className="flex-1 h-px bg-[var(--ff-border)]" />
        <span className="text-xs uppercase tracking-widest text-[var(--ff-text-muted)]">or</span>
        <span className="flex-1 h-px bg-[var(--ff-border)]" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <input className="input-base" placeholder="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required data-testid="login-email"/>
        <input className="input-base" placeholder="Password" type="password" value={password} onChange={e=>setPassword(e.target.value)} required data-testid="login-password"/>
        <TurnstileWidget onVerify={setTurnstileToken}/>
        {err && <p className="text-sm text-red-700" data-testid="login-error">{err}</p>}
        <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid="login-submit">
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <div className="text-right">
          <Link to="/forgot-password" className="text-xs underline text-[var(--ff-text-muted)]" data-testid="forgot-link">Forgot password?</Link>
        </div>
      </form>
      <p className="mt-8 text-center text-sm text-[var(--ff-text-secondary)]">
        New here? <Link to={`/register${sp.get("next") ? `?next=${encodeURIComponent(sp.get("next"))}` : ""}`} className="underline text-[var(--ff-primary)]" data-testid="login-to-register">Create account</Link>
      </p>
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const refFromUrl = sp.get("ref") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "customer", farm_name: "", referral_code: refFromUrl, sms_consent: false, sms_marketing_consent: false });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const payload = { ...form, turnstile_token: turnstileToken };
      if (payload.role !== "vendor") delete payload.farm_name;
      if (!payload.referral_code) delete payload.referral_code;
      if (!payload.phone) { delete payload.phone; payload.sms_consent = false; payload.sms_marketing_consent = false; }
      const u = await register(payload);
      const next = sp.get("next");
      nav(next || (u.role === "vendor" ? "/vendor" : "/pricing"));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="register-page">
      <p className="overline text-center">Join the cooperative</p>
      <h1 className="font-display text-4xl text-center mt-3">Create your account.</h1>

      <div className="mt-10">
        <GoogleAuthButton label="Sign up with Google" />
      </div>

      <div className="my-6 flex items-center gap-3">
        <span className="flex-1 h-px bg-[var(--ff-border)]" />
        <span className="text-xs uppercase tracking-widest text-[var(--ff-text-muted)]">or</span>
        <span className="flex-1 h-px bg-[var(--ff-border)]" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <input className="input-base" placeholder="Full name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} required data-testid="reg-name"/>
        <input className="input-base" placeholder="Email" type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} required data-testid="reg-email"/>
        <input className="input-base" placeholder="Phone number (for text updates — optional)" type="tel" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value, sms_consent: e.target.value ? form.sms_consent : false, sms_marketing_consent: e.target.value ? form.sms_marketing_consent : false})} data-testid="reg-phone"/>
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="reg-sms-consent-label">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0 accent-[var(--ff-primary)]"
            checked={form.sms_consent}
            disabled={!form.phone}
            onChange={e=>setForm({...form, sms_consent: e.target.checked})}
            data-testid="reg-sms-consent"
          />
          <span className="text-xs leading-relaxed text-[var(--ff-text-secondary)]">
            I agree to receive order &amp; account texts (delivery updates, receipts) from The Farm To You at the number above. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel.
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="reg-sms-marketing-consent-label">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0 accent-[var(--ff-primary)]"
            checked={form.sms_marketing_consent}
            disabled={!form.phone}
            onChange={e=>setForm({...form, sms_marketing_consent: e.target.checked})}
            data-testid="reg-sms-marketing-consent"
          />
          <span className="text-xs leading-relaxed text-[var(--ff-text-secondary)]">
            I agree to receive marketing &amp; promotional texts (deals, new drops) from The Farm To You at the number above. Consent is not a condition of purchase. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel. See our{" "}
            <Link to="/terms#sms" target="_blank" className="underline text-[var(--ff-primary)]">Terms</Link> &amp;{" "}
            <Link to="/privacy#sms" target="_blank" className="underline text-[var(--ff-primary)]">Privacy Policy</Link>.
          </span>
        </label>
        <input className="input-base" placeholder="Password (6+ chars)" type="password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} required minLength={6} data-testid="reg-password"/>
        <select className="input-base" value={form.role} onChange={e=>setForm({...form, role:e.target.value})} data-testid="reg-role">
          <option value="customer">I'm a customer</option>
          <option value="vendor">I'm a farmer / vendor</option>
        </select>
        {form.role === "vendor" && (
          <input className="input-base" placeholder="Farm / business name" value={form.farm_name} onChange={e=>setForm({...form, farm_name:e.target.value})} data-testid="reg-farm-name"/>
        )}
        {(form.referral_code || refFromUrl) && (
          <div className="card-flat p-3 bg-[var(--ff-citrus)]/10 border-[var(--ff-secondary)]/30" data-testid="reg-referral-applied">
            <p className="text-xs text-[var(--ff-text-secondary)]">
              Referred by code <strong className="font-mono">{form.referral_code || refFromUrl}</strong> — you'll both earn $10 credit when your membership activates.
            </p>
          </div>
        )}
        <TurnstileWidget onVerify={setTurnstileToken}/>
        {err && <p className="text-sm text-red-700" data-testid="reg-error">{err}</p>}
        <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid="reg-submit">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
      <p className="mt-8 text-center text-sm text-[var(--ff-text-secondary)]">
        Already a member? <Link to={`/login${sp.get("next") ? `?next=${encodeURIComponent(sp.get("next"))}` : ""}`} className="underline text-[var(--ff-primary)]">Sign in</Link>
      </p>
    </div>
  );
}
